/**
 * Elective Courses catalog — the 6-week applied-learning sprints (Read → Understand → Do).
 * Browse courses, see access/progress, unlock via standalone ($149) or Tier-2 add-on ($49/mo).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Target, Briefcase, Video, BookOpen, Brain, Rocket, Lock, CheckCircle2, ArrowRight, Award, GraduationCap, Zap } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { AppliedMasteryElective } from '../components/AppliedMasteryBanner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const BADGE_ICON = { target: Target, briefcase: Briefcase, video: Video };
const PHASE_ICON = { 1: BookOpen, 2: Brain, 3: Rocket };

const ElectiveCourses = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState('');
  const [siqBuying, setSiqBuying] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/academy/electives', { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) { navigate('/academy/login'); return; }
      toast.error('Could not load electives');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const checkout = async (courseId, mode) => {
    setBuying(`${courseId}-${mode}`);
    try {
      const r = await api.post('/academy/elective-courses/checkout', { course_id: courseId, mode, origin_url: window.location.origin }, { headers: authHeaders() });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally { setBuying(''); }
  };

  const open = (courseId) => navigate((data?.courses || []).find((x) => x.id === courseId)?.route || `/academy/elective-courses/${courseId}`);

  // Sport IQ — checkout for a single sport (mode: 'drip' | 'full')
  const siqCheckout = async (sport, mode) => {
    setSiqBuying(`${sport}-${mode}`);
    try {
      const r = await api.post(`/academy/sport-iq/${sport}/checkout`,
        { mode, origin_url: window.location.origin },
        { headers: authHeaders() });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally { setSiqBuying(''); }
  };

  // Poll Stripe status when returning from checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('sport_iq_purchase') === 'success' && params.get('session_id')) {
      const sid = params.get('session_id');
      api.get(`/academy/sport-iq/status/${sid}`, { headers: authHeaders() })
        .then((r) => {
          if (r.data.granted) {
            toast.success(`Unlocked ${r.data.sport} — ${r.data.mode.toUpperCase()} access`);
            load();
          }
        })
        .catch(() => {})
        .finally(() => {
          window.history.replaceState({}, '', '/academy/elective-courses');
        });
    }
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
  );

  return (
    <div className="min-h-screen text-white pt-16" style={{ backgroundColor: '#050508' }} data-testid="elective-courses-page">
      {/* Hero */}
      <div className="relative py-14 px-6 border-b-2 border-zinc-900" style={{ background: 'linear-gradient(to bottom, #0A0A0E, #050508)' }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(249,115,22,1) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,1) 1px, transparent 1px)', backgroundSize: '54px 54px' }} />
        <div className="relative container mx-auto max-w-5xl">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-orange-500 font-black"><GraduationCap className="w-3.5 h-3.5" /> Elective Academy</div>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight mt-2">6-Week <span className="text-orange-500">Applied Electives</span></h1>
          <p className="text-zinc-400 text-sm max-w-2xl mt-3">Read &rarr; Understand &rarr; Do. Each elective is a six-week sprint that ends with a real capstone, an official certificate, and a badge stamp on your Scholar Card.</p>
          {(data?.badges?.length > 0 || data?.credits?.length > 0) && (
            <div className="flex flex-wrap items-center gap-3 mt-5" data-testid="elective-earned-summary">
              {data.badges.map((b) => (
                <div key={b.key} className="flex items-center gap-1.5 border-2 border-amber-500/50 bg-amber-500/10 px-3 py-1.5" data-testid={`earned-badge-${b.key}`}>
                  <Award className="w-3.5 h-3.5 text-amber-400" /><span className="text-[10px] font-black uppercase tracking-widest text-amber-300">{b.name}</span>
                </div>
              ))}
              {data.credits?.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-zinc-400"><span className="font-black text-white">{data.credits.reduce((s, c) => s + (c.amount || 0), 0)}</span> Elective Credits earned</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Courses */}
      <div className="container mx-auto max-w-5xl px-6 pt-8">
        <AppliedMasteryElective />
      </div>

      {/* Sport IQ Track — free-access companion courses */}
      {data?.sport_iq_courses?.length > 0 && (
        <div className="container mx-auto max-w-5xl px-6 pt-8" data-testid="sport-iq-track">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-orange-500 font-black">Sport IQ Track · Free Access</div>
              <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight mt-1">The IQ Courses <span className="text-orange-500">by Coach B</span></h2>
              <p className="text-zinc-400 text-sm mt-2 max-w-2xl">Applied Mastery, sport by sport. Glossary, course modules, and Combine practice — all cross-linked and always growing.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {data.sport_iq_courses.map((s) => {
              const acc = s.access || { access: false, mode: null, unlocked_week: 0 };
              const price = s.pricing || {};
              const total = s.total_weeks || 6;
              const modeLabel = acc.mode === 'admin' ? 'Comp · Full Access'
                              : acc.mode === 'full' ? 'Full Access'
                              : acc.mode === 'drip' ? `Drip · Week ${acc.unlocked_week}/${total}`
                              : null;
              return (
              <div key={s.id} className="border-2 border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={`sport-iq-card-${s.id}`} style={{ borderTopColor: s.accent, borderTopWidth: 3 }}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-xl font-black text-white leading-tight">{s.title}</h3>
                    <span className="text-[9px] font-black uppercase tracking-widest border-2 px-2 py-1 whitespace-nowrap" style={{ borderColor: `${s.accent}88`, color: s.accent }}>
                      {s.module_count} Modules · {s.term_count} Terms
                    </span>
                  </div>
                  <p className="text-sm mt-1" style={{ color: s.accent }}>{s.tagline}</p>
                  <p className="text-zinc-400 text-xs mt-3 leading-relaxed">{s.description}</p>

                  {/* Access state pill */}
                  {modeLabel && (
                    <div className="mt-3 flex items-center gap-2" data-testid={`sport-iq-access-${s.id}`}>
                      <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest border-2 border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-2.5 py-1">
                        <CheckCircle2 className="w-3 h-3" /> {modeLabel}
                      </span>
                    </div>
                  )}

                  {acc.access ? (
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <button
                        onClick={() => navigate(s.course_url)}
                        className="flex items-center justify-center gap-1.5 bg-orange-500 hover:bg-orange-400 text-black px-3 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors"
                        data-testid={`sport-iq-open-course-${s.id}`}
                      >
                        Open Course <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => navigate(s.glossary_url)}
                        className="flex items-center justify-center gap-1.5 border-2 border-zinc-700 text-zinc-300 hover:border-white px-3 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors"
                        data-testid={`sport-iq-open-glossary-${s.id}`}
                      >
                        Browse Glossary
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mt-4 mb-2">
                        <Lock className="w-3 h-3" /> Locked · Choose Your Access
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => siqCheckout(s.sport, 'full')}
                          disabled={siqBuying === `${s.sport}-full`}
                          className="flex flex-col items-center justify-center gap-1 px-3 py-3 text-black transition-colors disabled:opacity-60"
                          style={{ backgroundColor: s.accent }}
                          data-testid={`sport-iq-buy-full-${s.id}`}
                        >
                          {siqBuying === `${s.sport}-full` ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                            <>
                              <span className="text-[10px] font-black uppercase tracking-widest">Full · ${price.full_onetime}</span>
                              <span className="text-[9px] font-bold uppercase tracking-wider opacity-80">Skip Around · One-Time</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => siqCheckout(s.sport, 'drip')}
                          disabled={siqBuying === `${s.sport}-drip`}
                          className="flex flex-col items-center justify-center gap-1 border-2 hover:bg-white/5 px-3 py-3 transition-colors disabled:opacity-60"
                          style={{ borderColor: s.accent, color: s.accent }}
                          data-testid={`sport-iq-buy-drip-${s.id}`}
                        >
                          {siqBuying === `${s.sport}-drip` ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                            <>
                              <span className="text-[10px] font-black uppercase tracking-widest">Drip · ${price.drip_monthly}/mo</span>
                              <span className="text-[9px] font-bold uppercase tracking-wider opacity-80">1 Week / Week · Cancel Anytime</span>
                            </>
                          )}
                        </button>
                      </div>
                      <button
                        onClick={() => navigate(s.glossary_url)}
                        className="w-full flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2 mt-2"
                        data-testid={`sport-iq-preview-glossary-${s.id}`}
                      >
                        <Zap className="w-3 h-3" /> Preview: Browse the Free Glossary
                      </button>
                    </>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="container mx-auto max-w-5xl px-6 py-12 space-y-8">
        <div className="pt-4">
          <div className="text-[10px] tracking-[0.35em] uppercase text-zinc-500 font-black">6-Week Applied Electives · Read → Understand → Do</div>
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight mt-1">Certificate <span className="text-orange-500">Electives</span></h2>
        </div>
        {data?.courses?.map((c) => {
          const BadgeIcon = BADGE_ICON[c.badge?.icon] || Award;
          const done = c.progress?.completed;
          return (
            <div key={c.id} className="border-2 border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={`elective-card-${c.id}`} style={{ borderTopColor: c.accent, borderTopWidth: 3 }}>
              <div className="grid md:grid-cols-3">
                {/* Left: info */}
                <div className="md:col-span-2 p-6">
                  {c.cover_image && (
                    <img src={c.cover_image} alt={c.title} className="w-full h-40 object-cover mb-4 border border-zinc-800" data-testid={`elective-cover-${c.id}`} />
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-2xl font-black text-white leading-tight">{c.title}</h2>
                      <p className="text-sm mt-1" style={{ color: c.accent }}>{c.tagline}</p>
                    </div>
                    {done && <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-400 border-2 border-emerald-500/40 px-2 py-1 whitespace-nowrap"><CheckCircle2 className="w-3.5 h-3.5" /> Completed</span>}
                  </div>
                  <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{c.description}</p>

                  {/* 3-phase strip */}
                  <div className="grid grid-cols-3 gap-2 mt-5">
                    {c.phases.map((p) => {
                      const PI = PHASE_ICON[p.phase] || BookOpen;
                      return (
                        <div key={p.phase} className="border border-zinc-800 bg-black/40 p-3">
                          <PI className="w-4 h-4" style={{ color: c.accent }} />
                          <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500 mt-2">{p.weeks}</div>
                          <div className="text-xs font-bold text-white mt-0.5">{p.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* progress */}
                  {c.access && c.progress?.status !== 'not_started' && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1"><span>Progress</span><span>{c.progress.pct}%</span></div>
                      <div className="h-1.5 bg-zinc-900"><div className="h-full" style={{ width: `${c.progress.pct}%`, backgroundColor: c.accent }} /></div>
                    </div>
                  )}
                </div>

                {/* Right: badge + CTA */}
                <div className="border-t-2 md:border-t-0 md:border-l-2 border-zinc-800 p-6 flex flex-col justify-between bg-black/30">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center border-2" style={{ borderColor: c.accent, backgroundColor: `${c.accent}22` }}>
                        <BadgeIcon className="w-5 h-5" style={{ color: c.accent }} />
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-zinc-500">Earn the badge</div>
                        <div className="text-xs font-black text-white leading-tight">{c.badge?.name}</div>
                      </div>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-3">+{c.credit_amount} {c.credit_type} Credit on your transcript</div>
                  </div>

                  <div className="mt-5 space-y-2">
                    {c.access ? (
                      <button onClick={() => open(c.id)} className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-black px-4 py-3 text-[11px] font-black uppercase tracking-widest transition-colors" data-testid={`elective-open-${c.id}`}>
                        {done ? 'Review Course' : c.progress?.status === 'not_started' ? 'Start Course' : 'Continue'} <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500"><Lock className="w-3 h-3" /> Locked</div>
                        <button onClick={() => checkout(c.id, 'standalone')} disabled={buying === `${c.id}-standalone`} className="w-full flex items-center justify-center gap-2 border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-black px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid={`elective-buy-standalone-${c.id}`}>
                          {buying === `${c.id}-standalone` ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Unlock · ${data.pricing.standalone}</>}
                        </button>
                        <button onClick={() => checkout(c.id, 'addon')} disabled={buying === `${c.id}-addon`} className="w-full flex items-center justify-center gap-2 border-2 border-zinc-700 text-zinc-300 hover:border-white px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid={`elective-buy-addon-${c.id}`}>
                          {buying === `${c.id}-addon` ? <Loader2 className="w-4 h-4 animate-spin" /> : <>All Electives Add-On · ${data.pricing.addon_monthly}/mo</>}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <p className="text-center text-[11px] text-zinc-600 pt-4">Included free with the <span className="text-zinc-400 font-bold">Live Classroom Hybrid Stream</span> tier · Add-on to <span className="text-zinc-400 font-bold">Full Virtual Private School</span> for ${data?.pricing?.addon_monthly}/mo</p>
      </div>
    </div>
  );
};

export default ElectiveCourses;
